import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { KineticHeading } from "../components/site/KineticHeading";
import { useContent } from "../context/ContentContext";
import { img } from "../lib/api";

const CATEGORIES = ["All", "EVENT CORE", "EXPERIENCE CORE", "TECH CORE"];

export default function Products() {
  const { products } = useContent();
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "All";
  const [active, setActive] = useState(CATEGORIES.includes(initial) ? initial : "All");

  useEffect(() => {
    const c = params.get("category");
    if (c && CATEGORIES.includes(c)) setActive(c);
  }, [params]);

  const filtered = active === "All" ? products : products.filter((p) => p.category === active);

  const selectCategory = (cat) => {
    setActive(cat);
    if (cat === "All") setParams({}); else setParams({ category: cat });
  };

  return (
    <>
      <section className="mx-auto max-w-[1400px] px-6 pb-12 pt-40 md:px-10 md:pt-48" data-testid="products-hero">
        <div className="mb-6 text-xs font-bold uppercase tracking-[0.25em] text-volt">Products</div>
        <KineticHeading lines={["The product box."]}
          className="font-display text-5xl font-semibold uppercase leading-[0.95] tracking-tighter sm:text-7xl lg:text-8xl" />
        <p className="mt-8 max-w-2xl text-lg text-white/60">
          {products.length} production-ready products you can deploy at your next event. Filter, pick, brief us — we'll do the rest.
        </p>
      </section>

      <div className="sticky top-[73px] z-30 border-y border-white/10 bg-korio-bg/80 backdrop-blur-xl" data-testid="products-filters">
        <div className="mx-auto flex max-w-[1400px] flex-wrap gap-2 px-6 py-4 md:px-10">
          {CATEGORIES.map((cat) => (
            <button key={cat} onClick={() => selectCategory(cat)}
              data-testid={`filter-${cat.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}
              className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors duration-300 ${active === cat ? "border-transparent brand-gradient text-white" : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"}`}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-[1400px] px-6 py-16 md:px-10 md:py-24" data-testid="products-grid">
        <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.div key={p.id || p.name} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.04 }} data-testid={`product-card-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-korio-surface">
                <Link to={`/products/${p.slug || p.id}`} data-testid={`product-link-${i}`} aria-label={p.name} className="absolute inset-0 z-10" />
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={img(p.image)} alt={p.name} loading="lazy" className="h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-black/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white/80 backdrop-blur">{p.category}</span>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl font-medium tracking-tight text-white">{p.name}</h3>
                  <p className="mt-2 text-sm text-white/55">{p.desc}</p>
                </div>
                <span className="absolute bottom-5 right-5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:border-volt group-hover:bg-volt group-hover:text-black">
                  <ArrowUpRight size={16} />
                </span>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section className="border-t border-white/10 bg-korio-surface py-24 text-center" data-testid="products-cta">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <h2 className="font-display text-4xl font-medium tracking-tight sm:text-5xl">
            Found what you need? <span className="text-stroke">Brief us.</span>
          </h2>
          <Link to="/contact" className="brand-gradient mt-8 inline-flex rounded-full px-8 py-4 font-semibold text-white transition-transform hover:-translate-y-0.5">Start a project</Link>
        </div>
      </section>
    </>
  );
}
